import {
  type CanvasRenderer,
  type ElementRenderer,
  ElementRendererExtension,
} from '@blocksuite/affine-block-surface';
import {
  ColorScheme,
  type MermaidElementModel,
} from '@blocksuite/affine-model';
import {
  MermaidRenderProvider,
  type MermaidRenderSession,
  translate,
} from '@blocksuite/affine-shared/services';

import { trackMermaidAction } from '../telemetry.js';
import {
  getMermaidIntrinsicSize,
  getMermaidRenderStateSignal,
  setMermaidInteractionSnapshot,
  setMermaidRenderPending,
  setMermaidRenderResult,
} from './state.js';

type MermaidCanvasState = {
  debounceTimer: ReturnType<typeof setTimeout> | null;
  image: HTMLImageElement | null;
  imageKey: string | null;
  pendingKey: string | null;
  request: number;
  session: MermaidRenderSession | null;
  settledKey: string | null;
};

const MERMAID_EDIT_RENDER_DEBOUNCE_MS = 220;

const states = new WeakMap<MermaidElementModel, MermaidCanvasState>();

function getState(model: MermaidElementModel) {
  let state = states.get(model);
  if (!state) {
    state = {
      debounceTimer: null,
      image: null,
      imageKey: null,
      pendingKey: null,
      request: 0,
      session: null,
      settledKey: null,
    };
    states.set(model, state);
  }
  return state;
}

function makeRenderKey(model: MermaidElementModel, renderer: CanvasRenderer) {
  return JSON.stringify({
    source: model.source.toString(),
    themeMode: model.themeMode,
    prefersDark: renderer.getColorScheme() === ColorScheme.Dark,
  });
}

function loadSvgImage(svg: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    const blob = new Blob([svg], {
      type: 'image/svg+xml;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);

    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to decode Mermaid SVG.'));
    };
    image.src = url;
  });
}

function ensureRender(
  model: MermaidElementModel,
  renderer: CanvasRenderer,
  state: MermaidCanvasState,
  key: string
) {
  if (state.pendingKey === key || state.settledKey === key) return;

  const provider = renderer.std.getOptional(MermaidRenderProvider);
  if (!provider) return;

  if (state.debounceTimer) {
    clearTimeout(state.debounceTimer);
  }

  const source = model.source.toString();
  state.pendingKey = key;
  const request = ++state.request;
  setMermaidRenderPending(model, source);

  state.debounceTimer = setTimeout(() => {
    state.debounceTimer = null;
    if (request !== state.request) return;

    state.session ??= provider.createSession();
    state.session
      .render({
        source,
        themeMode: model.themeMode,
        prefersDark: renderer.getColorScheme() === ColorScheme.Dark,
        visible: true,
        zoom: renderer.viewport.zoom,
      })
      .then(async result => {
        if (request !== state.request) return;

        setMermaidRenderResult(model, source, result);
        if (result.status === 'invalid' || result.status === 'error') {
          trackMermaidAction(renderer.std, 'render-error', result.status);
        }

        if (
          result.status !== 'ready' &&
          result.status !== 'degraded' &&
          !(
            (result.status === 'invalid' || result.status === 'error') &&
            result.svg
          )
        ) {
          state.pendingKey = null;
          if (
            result.status === 'invalid' ||
            result.status === 'error' ||
            result.status === 'empty'
          ) {
            state.settledKey = key;
          }
          return;
        }

        const svg = result.svg;
        if (!svg) {
          state.pendingKey = null;
          state.settledKey = key;
          return;
        }

        const image = await loadSvgImage(svg);
        if (request !== state.request) return;

        if (result.status === 'ready' || result.status === 'degraded') {
          void import('./interaction-geometry.js')
            .then(({ extractMermaidInteractionSnapshot }) => {
              if (request !== state.request) return;
              const snapshot = extractMermaidInteractionSnapshot(
                svg,
                source,
                key
              );
              if (request !== state.request) return;
              setMermaidInteractionSnapshot(model, snapshot);
              renderer.refresh({ type: 'element', element: model });
            })
            .catch(() => {
              if (request === state.request) {
                setMermaidInteractionSnapshot(model, null);
              }
            });
        } else {
          setMermaidInteractionSnapshot(model, null);
        }

        state.image = image;
        state.imageKey = key;
        state.pendingKey = null;
        state.settledKey = key;
        renderer.refresh({ type: 'element', element: model });
      })
      .catch(error => {
        if (request === state.request) {
          state.pendingKey = null;
        }
        console.error('Failed to render Mermaid canvas element', error);
      });
  }, MERMAID_EDIT_RENDER_DEBOUNCE_MS);
}

function drawInvalidState(
  model: MermaidElementModel,
  ctx: CanvasRenderingContext2D,
  renderer: CanvasRenderer
) {
  const renderState = getMermaidRenderStateSignal(model).value;
  if (renderState.status !== 'invalid' && renderState.status !== 'error')
    return;

  const [, , w] = model.deserializedXYWH;
  const label =
    translate(
      renderer.std,
      renderState.status === 'invalid'
        ? 'Invalid Mermaid syntax'
        : 'Mermaid render error'
    ) ??
    (renderState.status === 'invalid'
      ? 'Invalid Mermaid syntax'
      : 'Mermaid render error');

  ctx.save();
  ctx.font = '12px sans-serif';
  const width = Math.min(
    Math.max(ctx.measureText(label).width + 20, 96),
    w - 16
  );
  ctx.fillStyle = renderer.getPropertyValue(
    '--affine-background-overlay-panel-color'
  );
  ctx.fillRect(8, 8, width, 28);
  ctx.fillStyle = renderer.getPropertyValue('--affine-error-color');
  ctx.fillText(label, 18, 27, Math.max(0, width - 20));
  ctx.restore();
}

function drawPendingPlaceholder(
  model: MermaidElementModel,
  ctx: CanvasRenderingContext2D,
  renderer: CanvasRenderer
) {
  const [, , w, h] = model.deserializedXYWH;
  ctx.save();
  ctx.strokeStyle = renderer.getPropertyValue('--affine-border-color');
  ctx.setLineDash([6, 6]);
  ctx.strokeRect(0, 0, w, h);
  ctx.restore();
}

export const getMermaidCanvasIntrinsicSize = getMermaidIntrinsicSize;

export function disposeMermaidCanvasState(model: MermaidElementModel) {
  setMermaidInteractionSnapshot(model, null);
  const state = states.get(model);
  if (!state) return;
  state.request++;
  if (state.debounceTimer) {
    clearTimeout(state.debounceTimer);
  }
  state.session?.dispose();
  states.delete(model);
}

export const mermaidCanvasRenderer: ElementRenderer<MermaidElementModel> = (
  model,
  ctx,
  matrix,
  renderer
) => {
  const state = getState(model);
  const key = makeRenderKey(model, renderer);
  ensureRender(model, renderer, state, key);

  ctx.setTransform(matrix);

  if (!state.image) {
    drawPendingPlaceholder(model, ctx, renderer);
    return;
  }

  const [, , w, h] = model.deserializedXYWH;
  ctx.drawImage(state.image, 0, 0, w, h);
  drawInvalidState(model, ctx, renderer);
};

export const MermaidElementRendererExtension = ElementRendererExtension(
  'mermaid',
  mermaidCanvasRenderer
);
